/**
 * Lifecycle History as people should read it (Traci, 5 Oct 2026: "Looks like the lifecycle history is duplicating").
 *
 * Two things went wrong in booking_stage_history until Oct 2026, and the rows are left as they are:
 *   Duplicates      the database trigger logs every stage change, and the page that made the change logged it again,
 *                   a fraction of a second apart (192 pairs on 166 bookings since 1 Sep). One read "Manual override",
 *                   the other "Stage change".
 *   Carried reasons the trigger copied the booking's last manual reason onto every later change, automatic ones
 *                   included, and called them manual (Melissa Boban: the 10/7 move to Media Uploaded showed Laura's 9/23
 *                   questionnaire note).
 * So the views show one row per change and no carried-over reason. Pure.
 */
const SAME_CHANGE_MS = 5000
const time = v => { const t = Date.parse(v || ''); return Number.isFinite(t) ? t : 0 }

/** Which of two rows for the same change to keep: the one with a reason, then manual, then the one with a person. */
const score = r => (r.reason ? 4 : 0) + (r.change_type === 'manual' ? 2 : 0) + (r.changed_by ? 1 : 0)

/**
 * @param rows booking_stage_history rows (any order, any number of bookings)
 * @returns the same rows in the same order, one per real change, carried reasons removed
 */
export function cleanStageHistory(rows) {
  const list = Array.isArray(rows) ? rows : []
  const ordered = [...list].sort((a, b) => String(a.booking_id || '').localeCompare(String(b.booking_id || '')) || time(a.changed_at) - time(b.changed_at))
  // 1. Group the rows that are one change (same booking and stage, seconds apart)
  const changes = []
  for (const r of ordered) {
    const cur = changes[changes.length - 1]
    const head = cur?.[0]
    if (head && head.booking_id === r.booking_id && head.to_stage === r.to_stage && Math.abs(time(r.changed_at) - time(head.changed_at)) <= SAME_CHANGE_MS) cur.push(r)
    else changes.push([r])
  }
  // 2. Keep the best row of each; a lone trigger row repeating the booking's previous reason is a carried reason. A
  //    change someone really made also has the page's own row next to it, so a reason typed again is kept.
  const out = new Map()
  const lastReason = new Map()
  for (const group of changes) {
    const best = group.reduce((a, b) => (score(b) > score(a) ? b : a))
    const prev = lastReason.get(best.booking_id)
    const carried = group.length === 1 && best.reason && best.trigger_source === 'booking_update' && prev && prev.reason === best.reason && time(best.changed_at) - time(prev.changed_at) > 60000
    if (carried) out.set(best, { ...best, reason: null, change_type: 'auto', changed_by: null })
    else { out.set(best, best); if (best.reason) lastReason.set(best.booking_id, best) }
  }
  return list.filter(r => out.has(r)).map(r => out.get(r))
}
